import { DateTime, encodeToQuerystring, Session } from '@benzinga/session';
import { StockSymbol } from '@benzinga/session';

import { WatchlistManager, WatchlistSymbol } from '@benzinga/watchlist-manager';
import { AutocompleteItem } from '@benzinga/autocomplete-manager';
import { CalendarType, CalendarDataParameters, CalendarTransient } from './entities';
import { TickerSelectedEvent, WidgetLinkingManager } from '@benzinga/widget-linking';

export const CALENDAR_PAGESIZE = 10000;
export const CALENDAR_SCREEN_SIZE = 50;
export const reachALimitNotification = {
  description: `
    The result set was larger and capped at ${CALENDAR_PAGESIZE}.
    To see the full dataset, change the date range or limit by watchlist.`,
  message: 'Reach a limit',
};

interface Params {
  [key: string]: string | number | boolean | undefined | null | Params;
}

const RatingsFields =
  '*,ratings_accuracy.smart_score,ratings_accuracy.overall_success_rate,ratings_accuracy.total_ratings';

interface CalendarRequestData extends Params {
  date?: string;
  date_from?: string;
  date_sort?: string;
  date_to?: string;
  display: 'flat';
  tickers?: string;
  updated?: number;
}

export interface CalendarQuery extends Params {
  page: number;
  pagesize: number;
  parameters: CalendarRequestData;
}

export const calendarDefaultType = 'earnings';

export const calendarDataParameters: CalendarDataParameters = {
  calendarType: calendarDefaultType,
  dateEnd: DateTime.dateNow().toISODateString(),
  dateStart: DateTime.dateNow().toISODateString(),
  filters: [] as AutocompleteItem[],
  watchlistIds: [],
};

export const calendarTransient: CalendarTransient = {
  isLoading: false,
  lastUpdatedEventsIds: [],
};

const getApiUrlByCalendarType = (type: CalendarType, defaultType: CalendarType) =>
  type === 'optionsActivity'
    ? `${(window as any).env.API_ROOT}/api/v1/signal/option_activity`
    : type === 'fda'
      ? `${(window as any).env.API_ROOT}/api/v2.1/calendar/fda`
      : `${(window as any).env.API_ROOT}/api/v2/calendar/${type || defaultType}`;

export const getSymbolsFromParameters = (
  parameters: Partial<CalendarDataParameters>,
  session: Session,
): Set<StockSymbol> => {
  const symbols = new Set<StockSymbol>(
    parameters.filters?.flatMap(item => {
      if (item.type === 'symbol') return [item.tagId];
      else if (item.type === 'symbolLink') {
        const symbol = (
          session
            .getManager(WidgetLinkingManager)
            .getWidgetLinkFeedByID(item.tagId)
            ?.getLatestEvent('ticker_selected') as TickerSelectedEvent | undefined
        )?.symbol;
        return symbol ? [symbol] : [];
      } else return [];
    }) || [],
  );
  if (!symbols.size) {
    const addSymbol = (symbol: WatchlistSymbol) => symbols.add(symbol.symbol);
    parameters.watchlistIds?.forEach(watchlistId => {
      session.getManager(WatchlistManager).getStoredWatchlistById(watchlistId)?.symbols?.forEach(addSymbol);
    });
  }
  return symbols;
};

export const createCalendarUrl = (parameters: CalendarDataParameters, session: Session, pagesize: number): string => {
  const requestData: CalendarRequestData = {
    display: 'flat',
  };
  const symbols = [...getSymbolsFromParameters(parameters, session)];

  if (symbols?.length) {
    // important! the backend expects `tickers` not `symbols`
    requestData.tickers = symbols.join();
  }

  if (parameters.dateStart || parameters.dateEnd) {
    if (!parameters.dateStart) {
      if (parameters.dateEnd) {
        requestData.date_to = parameters.dateEnd;
      }
    } else if (!parameters.dateEnd) {
      requestData.date_from = parameters.dateStart;
      // if both dateStart && dateEnd
    } else if (parameters.dateStart === parameters.dateEnd) {
      requestData.date = parameters.dateStart;
    } else {
      requestData.date_from = parameters.dateStart;
      requestData.date_to = parameters.dateEnd;
    }

    // Ensure that the correct date field is used
    if (parameters.calendarType === 'dividends' || parameters.calendarType === 'splits') {
      requestData.date_search_field = 'ex';
    }
  }

  if (parameters.updated) {
    requestData.updated = parameters.updated;
  }

  const { calendarType } = parameters;

  const url = getApiUrlByCalendarType(calendarType, calendarDefaultType);

  let query: CalendarQuery = {
    page: 0,
    pagesize,
    parameters: requestData,
  };

  if (calendarType === 'ratings') {
    query = {
      fields: RatingsFields,
      ...query,
    };
  }

  if (calendarType === 'sec') {
    query = {
      ...query,
      token: (window as any).env.CALENDAR_KEY,
    };
  }

  return `${url}?${encodeToQuerystring(query)}`;
};
